<template>
  <div class="max-w-7xl mx-auto py-8">
    <!-- Page Header -->
    <div class="mb-8">
      <div class="bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl p-8 text-white shadow-xl">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold mb-2">Workshop Jobs</h1>
            <p class="text-orange-100 text-lg">Manage your assigned jobs with our Kanban board</p>
          </div>
          <div class="flex space-x-3">
            <button
              @click="refreshJobs"
              :disabled="loading"
              class="px-6 py-3 bg-white bg-opacity-20 backdrop-blur-sm rounded-xl hover:bg-opacity-30 transition-all duration-200 disabled:opacity-50"
            >
              {{ loading ? 'Loading...' : 'Refresh' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
      <div class="bg-white overflow-hidden shadow-lg rounded-xl">
        <div class="p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <svg class="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">Pending Jobs</p>
              <p class="text-2xl font-bold text-gray-900">{{ stats.pending || 0 }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-white overflow-hidden shadow-lg rounded-xl">
        <div class="p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">In Progress</p>
              <p class="text-2xl font-bold text-gray-900">{{ stats.in_workshop || 0 }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-white overflow-hidden shadow-lg rounded-xl">
        <div class="p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">Completed</p>
              <p class="text-2xl font-bold text-gray-900">{{ stats.completed || 0 }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-white overflow-hidden shadow-lg rounded-xl">
        <div class="p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg class="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">Total Jobs</p>
              <p class="text-2xl font-bold text-gray-900">{{ stats.total || 0 }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Search Filters -->
    <div class="bg-white rounded-lg shadow-sm border p-4 mb-6">
      <div class="flex flex-wrap gap-4 items-end">
        <!-- Order Number Search -->
        <div class="flex-1 min-w-64">
          <label class="block text-sm font-medium text-gray-700 mb-1">Order Number</label>
          <input
            v-model="searchQuery.orderNo"
            type="text"
            placeholder="Search by order #..."
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            @input="searchJobs"
          />
        </div>
        
        <!-- Clear Button -->
        <button
          @click="clearFilters"
          class="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
        >
          Clear Filters
        </button>
      </div>
    </div>

    <!-- Kanban Board -->
    <div class="bg-white shadow-lg rounded-xl overflow-hidden">
      <div class="px-6 py-4 border-b border-gray-200">
        <h3 class="text-lg font-semibold text-gray-900">Job Board</h3>
      </div>
      
      <div class="p-6">
        <div v-if="loading" class="text-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
          <p class="mt-2 text-gray-500">Loading jobs...</p>
        </div>
        
        <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- Pending Column -->
          <div class="bg-gray-50 rounded-lg p-4">
            <div class="flex items-center justify-between mb-4">
              <h4 class="text-lg font-semibold text-gray-900 flex items-center">
                <div class="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                Pending
                <span class="ml-2 bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full">
                  {{ getJobsByStatus('pending').length }}
                </span>
              </h4>
            </div>
            
            <div class="space-y-4">
              <div
                v-for="job in getJobsByStatus('pending')"
                :key="job.id"
                class="bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <!-- Job Header -->
                <div class="flex justify-between items-start mb-3">
                  <h5 class="font-medium text-gray-900">{{ job.job_id || `Job #${job.id}` }}</h5>
                  <span class="text-xs text-gray-500">Order #{{ job.order_no }}</span>
                </div>
                
                <!-- Job Info -->
                <div class="text-sm text-gray-600 mb-3">
                  <p class="mb-1"><span class="font-medium">Due:</span> {{ formatDate(job.due_date) }}</p>
                  <p class="mb-1"><span class="font-medium">Products:</span> {{ job.product_count || 0 }} {{ job.product_count === 1 ? 'product' : 'products' }}, {{ job.item_count || 0 }} {{ job.item_count === 1 ? 'item' : 'items' }}</p>
                </div>

                <!-- Product Details Toggle -->
                <div v-if="job.items && job.items.length > 0" class="mb-3">
                  <button
                    @click="toggleJobDetails(job.id)"
                    class="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    <svg 
                      class="w-4 h-4 mr-1 transition-transform"
                      :class="{ 'rotate-90': expandedJobs[job.id] }"
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                    {{ expandedJobs[job.id] ? 'Hide Details' : 'Show Details' }} ({{ job.product_count || 0 }} {{ job.product_count === 1 ? 'product' : 'products' }}, {{ job.item_count || 0 }} {{ job.item_count === 1 ? 'item' : 'items' }})
                  </button>
                </div>

                <!-- Collapsible Product Details -->
                <div v-if="job.items && job.items.length > 0 && expandedJobs[job.id]" class="space-y-3">
                  <div
                    v-for="item in job.items"
                    :key="item.id"
                    class="bg-gray-50 rounded-lg p-3"
                  >
                    <div class="flex gap-3">
                      <!-- Product Images -->
                      <div v-if="item.images && item.images.length > 0" class="flex-shrink-0">
                        <div class="relative group cursor-pointer" @click="viewImageModal(item.images, 0, item.name)" title="Click to view full size">
                          <img 
                            :src="getImageUrl(item.images[0])" 
                            :alt="item.name"
                            class="w-16 h-16 object-contain rounded-lg border border-gray-200 hover:opacity-80 hover:border-orange-500 hover:shadow-md transition-all duration-200"
                            @error="handleImageError"
                          />
                          <div v-if="item.images.length > 1" class="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                            +{{ item.images.length - 1 }}
                          </div>
                        </div>
                      </div>
                      
                      <!-- Product Details -->
                      <div class="flex-1 min-w-0">
                        <h6 class="font-medium text-gray-900 text-sm mb-1">{{ item.name }}</h6>
                        <div class="text-xs text-gray-600 space-y-1">
                          <p><span class="font-medium">SKU:</span> {{ item.sku }}</p>
                          <p><span class="font-medium">Qty:</span> {{ item.qty }}</p>
                          <p><span class="font-medium">Metal:</span> {{ item.metal }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Action Buttons -->
                <div class="flex flex-wrap gap-2 mt-3 whitespace-nowrap">
                  <button
                    @click="viewJobDetails(job.id)"
                    class="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    View Details
                  </button>
                  <LoadingButton
                    @click="updateJobStatus(job.id, 'in_workshop')"
                    :loading="jobLoadingStates[job.id]"
                    text="Start Work"
                    loading-text="Starting..."
                    variant="info"
                    size="sm"
                  />
                </div>
              </div>
              
              <div v-if="getJobsByStatus('pending').length === 0" class="text-center py-8 text-gray-500">
                <svg class="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p class="text-sm">No pending jobs</p>
              </div>
            </div>
          </div>

          <!-- In Progress Column -->
          <div class="bg-gray-50 rounded-lg p-4">
            <div class="flex items-center justify-between mb-4">
              <h4 class="text-lg font-semibold text-gray-900 flex items-center">
                <div class="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                In Progress
                <span class="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                  {{ getJobsByStatus('in_workshop').length }}
                </span>
              </h4>
            </div>
            
            <div class="space-y-4">
              <div
                v-for="job in getJobsByStatus('in_workshop')"
                :key="job.id"
                class="bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <!-- Job Header -->
                <div class="flex justify-between items-start mb-3">
                  <h5 class="font-medium text-gray-900">{{ job.job_id || `Job #${job.id}` }}</h5>
                  <span class="text-xs text-gray-500">Order #{{ job.order_no }}</span>
                </div>
                
                <!-- Job Info -->
                <div class="text-sm text-gray-600 mb-3">
                  <p class="mb-1"><span class="font-medium">Due:</span> {{ formatDate(job.due_date) }}</p>
                  <p class="mb-1"><span class="font-medium">Products:</span> {{ job.product_count || 0 }} {{ job.product_count === 1 ? 'product' : 'products' }}, {{ job.item_count || 0 }} {{ job.item_count === 1 ? 'item' : 'items' }}</p>
                </div>

                <!-- Product Details Toggle -->
                <div v-if="job.items && job.items.length > 0" class="mb-3">
                  <button
                    @click="toggleJobDetails(job.id)"
                    class="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    <svg 
                      class="w-4 h-4 mr-1 transition-transform"
                      :class="{ 'rotate-90': expandedJobs[job.id] }"
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                    {{ expandedJobs[job.id] ? 'Hide Details' : 'Show Details' }} ({{ job.product_count || 0 }} {{ job.product_count === 1 ? 'product' : 'products' }}, {{ job.item_count || 0 }} {{ job.item_count === 1 ? 'item' : 'items' }})
                  </button>
                </div>

                <!-- Collapsible Product Details -->
                <div v-if="job.items && job.items.length > 0 && expandedJobs[job.id]" class="space-y-3">
                  <div
                    v-for="item in job.items"
                    :key="item.id"
                    class="bg-gray-50 rounded-lg p-3"
                  >
                    <div class="flex gap-3">
                      <!-- Product Images -->
                      <div v-if="item.images && item.images.length > 0" class="flex-shrink-0">
                        <div class="relative group cursor-pointer" @click="viewImageModal(item.images, 0, item.name)" title="Click to view full size">
                          <img 
                            :src="getImageUrl(item.images[0])" 
                            :alt="item.name"
                            class="w-16 h-16 object-contain rounded-lg border border-gray-200 hover:opacity-80 hover:border-orange-500 hover:shadow-md transition-all duration-200"
                            @error="handleImageError"
                          />
                          <div v-if="item.images.length > 1" class="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                            +{{ item.images.length - 1 }}
                          </div>
                        </div>
                      </div>
                      
                      <!-- Product Details -->
                      <div class="flex-1 min-w-0">
                        <h6 class="font-medium text-gray-900 text-sm mb-1">{{ item.name }}</h6>
                        <div class="text-xs text-gray-600 space-y-1">
                          <p><span class="font-medium">SKU:</span> {{ item.sku }}</p>
                          <p><span class="font-medium">Qty:</span> {{ item.qty }}</p>
                          <p><span class="font-medium">Metal:</span> {{ item.metal }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Action Buttons -->
                <div class="flex flex-wrap gap-2 mt-3 whitespace-nowrap">
                  <button
                    @click="viewJobDetails(job.id)"
                    class="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    View Details
                  </button>
                  <LoadingButton
                    @click="updateJobStatus(job.id, 'completed')"
                    :loading="jobLoadingStates[job.id]"
                    text="Mark Complete"
                    loading-text="Completing..."
                    variant="success"
                    size="sm"
                  />
                  <LoadingButton
                    @click="updateJobStatus(job.id, 'pending')"
                    :loading="jobLoadingStates[job.id]"
                    text="Move to Pending"
                    loading-text="Moving..."
                    variant="warning"
                    size="sm"
                  />
                </div>
              </div>
              
              <div v-if="getJobsByStatus('in_workshop').length === 0" class="text-center py-8 text-gray-500">
                <svg class="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p class="text-sm">No jobs in progress</p>
              </div>
            </div>
          </div>

          <!-- Completed Column -->
          <div class="bg-gray-50 rounded-lg p-4">
            <div class="flex items-center justify-between mb-4">
              <h4 class="text-lg font-semibold text-gray-900 flex items-center">
                <div class="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                Completed
                <span class="ml-2 bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                  {{ getJobsByStatus('completed').length }}
                </span>
              </h4>
            </div>
            
            <div class="space-y-4">
              <div
                v-for="job in getJobsByStatus('completed')"
                :key="job.id"
                class="bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <!-- Job Header -->
                <div class="flex justify-between items-start mb-3">
                  <h5 class="font-medium text-gray-900">{{ job.job_id || `Job #${job.id}` }}</h5>
                  <span class="text-xs text-gray-500">Order #{{ job.order_no }}</span>
                </div>
                
                <!-- Job Info -->
                <div class="text-sm text-gray-600 mb-3">
                  <p class="mb-1"><span class="font-medium">Completed:</span> {{ formatDate(job.updated_at) }}</p>
                  <p class="mb-1"><span class="font-medium">Products:</span> {{ job.product_count || 0 }} {{ job.product_count === 1 ? 'product' : 'products' }}, {{ job.item_count || 0 }} {{ job.item_count === 1 ? 'item' : 'items' }}</p>
                </div>

                <!-- Product Details Toggle -->
                <div v-if="job.items && job.items.length > 0" class="mb-3">
                  <button
                    @click="toggleJobDetails(job.id)"
                    class="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    <svg 
                      class="w-4 h-4 mr-1 transition-transform"
                      :class="{ 'rotate-90': expandedJobs[job.id] }"
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                    {{ expandedJobs[job.id] ? 'Hide Details' : 'Show Details' }} ({{ job.product_count || 0 }} {{ job.product_count === 1 ? 'product' : 'products' }}, {{ job.item_count || 0 }} {{ job.item_count === 1 ? 'item' : 'items' }})
                  </button>
                </div>

                <!-- Collapsible Product Details -->
                <div v-if="job.items && job.items.length > 0 && expandedJobs[job.id]" class="space-y-3">
                  <div
                    v-for="item in job.items"
                    :key="item.id"
                    class="bg-gray-50 rounded-lg p-3"
                  >
                    <div class="flex gap-3">
                      <!-- Product Images -->
                      <div v-if="item.images && item.images.length > 0" class="flex-shrink-0">
                        <div class="relative group cursor-pointer" @click="viewImageModal(item.images, 0, item.name)" title="Click to view full size">
                          <img 
                            :src="getImageUrl(item.images[0])" 
                            :alt="item.name"
                            class="w-16 h-16 object-contain rounded-lg border border-gray-200 hover:opacity-80 hover:border-orange-500 hover:shadow-md transition-all duration-200"
                            @error="handleImageError"
                          />
                          <div v-if="item.images.length > 1" class="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                            +{{ item.images.length - 1 }}
                          </div>
                        </div>
                      </div>
                      
                      <!-- Product Details -->
                      <div class="flex-1 min-w-0">
                        <h6 class="font-medium text-gray-900 text-sm mb-1">{{ item.name }}</h6>
                        <div class="text-xs text-gray-600 space-y-1">
                          <p><span class="font-medium">SKU:</span> {{ item.sku }}</p>
                          <p><span class="font-medium">Qty:</span> {{ item.qty }}</p>
                          <p><span class="font-medium">Metal:</span> {{ item.metal }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Status -->
                <div class="flex items-center justify-between mt-3">
                  <span class="text-xs text-green-600 font-medium">✓ Completed</span>
                  <button
                    @click="viewJobDetails(job.id)"
                    class="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    View Details
                  </button>
                </div>
              </div>
              
              <div v-if="getJobsByStatus('completed').length === 0" class="text-center py-8 text-gray-500">
                <svg class="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                <p class="text-sm">No completed jobs</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Job Details Modal -->
    <div v-if="selectedJob" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div class="p-6">
          <div class="flex justify-between items-start mb-4">
            <h3 class="text-xl font-semibold text-gray-900">{{ selectedJob.job_id || `Job Details #${selectedJob.id}` }}</h3>
            <button
              @click="selectedJob = null"
              class="text-gray-400 hover:text-gray-600"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div class="space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <p class="text-sm font-medium text-gray-500">Order Number</p>
                <p class="text-gray-900">#{{ selectedJob.order_no }}</p>
              </div>
              <div>
                <p class="text-sm font-medium text-gray-500">Status</p>
                <span
                  :class="getStatusClass(selectedJob.status)"
                  class="px-2 py-1 text-xs font-medium rounded-full"
                >
                  {{ formatStatus(selectedJob.status) }}
                </span>
              </div>
              <div>
                <p class="text-sm font-medium text-gray-500">Due Date</p>
                <p class="text-gray-900">{{ formatDate(selectedJob.due_date) }}</p>
              </div>
            </div>
            
            <!-- Combined Totals Section -->
            <div v-if="selectedJob.items && selectedJob.items.length > 0" class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h4 class="font-semibold text-blue-900 mb-3">Order Summary</h4>
              <div class="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p class="text-sm text-blue-600">Total Quantity</p>
                  <p class="text-lg font-bold text-blue-900">{{ getTotalQuantity(selectedJob.items) }}</p>
                </div>
                <div>
                  <p class="text-sm text-blue-600">Total Gross Weight</p>
                  <p class="text-lg font-bold text-blue-900">{{ getTotalGrossWeight(selectedJob.items) }}g</p>
                </div>
                <div>
                  <p class="text-sm text-blue-600">Total Net Weight</p>
                  <p class="text-lg font-bold text-blue-900">{{ getTotalNetWeight(selectedJob.items) }}g</p>
                </div>
              </div>
            </div>

            <div v-if="selectedJob.items && selectedJob.items.length > 0">
              <h4 class="font-medium text-gray-900 mb-2">Items</h4>
              <div class="space-y-4">
                <div
                  v-for="item in selectedJob.items"
                  :key="item.id"
                  class="bg-gray-50 rounded-lg p-4"
                >
                  <div class="flex gap-4">
                    <!-- Product Images -->
                    <div v-if="item.images && item.images.length > 0" class="flex-shrink-0">
                        <div class="relative group cursor-pointer" @click="() => { console.log('Image clicked!', item.images); viewImageModal(item.images, 0, item.name); }" title="Click to view full size">
                        <img 
                          :src="getImageUrl(item.images[0])" 
                          :alt="item.name"
                          class="w-20 h-20 object-contain rounded-lg border border-gray-200 hover:opacity-80 hover:border-orange-500 hover:shadow-md transition-all duration-200"
                          @error="handleImageError"
                        />
                        <!-- Click indicator overlay -->
                        <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 rounded-lg flex items-center justify-center pointer-events-none">
                          <svg class="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                          </svg>
                        </div>
                        <!-- Click hint -->
                        <div class="absolute bottom-1 left-1 bg-orange-500 text-white text-xs px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                          Click
                        </div>
                        <div v-if="item.images.length > 1" class="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                          +{{ item.images.length - 1 }}
                        </div>
                      </div>
                    </div>
                    
                    <!-- Product Details -->
                    <div class="flex-1">
                      <div class="flex justify-between items-start">
                        <div>
                          <p class="font-medium text-gray-900">{{ item.name }}</p>
                          <p class="text-sm text-gray-600">SKU: {{ item.sku }}</p>
                          <p class="text-sm text-gray-600">Category: {{ item.category }}</p>
                          <p class="text-sm text-gray-600">Metal: {{ item.metal }}</p>
                          <div class="mt-2 space-y-1">
                            <p class="text-sm text-gray-600">
                              <span class="font-medium">Gross Weight:</span> {{ item.gross_weight || 0 }}g
                            </p>
                            <p class="text-sm text-gray-600">
                              <span class="font-medium">Net Weight:</span> {{ item.net_weight || 0 }}g
                            </p>
                          </div>
                        </div>
                        <div class="text-right">
                          <p class="text-sm font-medium text-gray-900">Qty: {{ item.quantity || item.qty }}</p>
                          <p class="text-sm text-gray-600">
                            <span class="font-medium">Gross Total:</span> {{ getItemGrossTotal(item) }}g
                          </p>
                          <p class="text-sm text-gray-600">
                            <span class="font-medium">Net Total:</span> {{ getItemNetTotal(item) }}g
                          </p>
                        </div>
                      </div>
                      
                      <!-- Additional Images Gallery -->
                      <div v-if="item.images && item.images.length > 1" class="mt-2">
                        <div class="flex gap-2 overflow-x-auto">
                          <img 
                            v-for="(image, index) in item.images.slice(1, 4)" 
                            :key="index"
                            :src="getImageUrl(image)" 
                            :alt="`${item.name} - Image ${index + 2}`"
                            class="w-12 h-12 object-contain rounded border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                            @click="viewImageModal(item.images, index + 1)"
                            @error="handleImageError"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div v-if="selectedJob.notes">
              <h4 class="font-medium text-gray-900 mb-2">Notes</h4>
              <p class="text-gray-600">{{ selectedJob.notes }}</p>
            </div>
            
            <div class="flex space-x-3 pt-4 border-t">
              <LoadingButton
                v-if="selectedJob.status === 'pending'"
                @click="updateJobStatus(selectedJob.id, 'in_workshop')"
                :loading="jobLoadingStates[selectedJob.id]"
                text="Start Work"
                loading-text="Starting..."
                variant="info"
                size="md"
              />
              <LoadingButton
                v-if="selectedJob.status === 'in_workshop'"
                @click="updateJobStatus(selectedJob.id, 'completed')"
                :loading="jobLoadingStates[selectedJob.id]"
                text="Mark Complete"
                loading-text="Completing..."
                variant="success"
                size="md"
              />
              <LoadingButton
                v-if="selectedJob.status === 'in_workshop'"
                @click="updateJobStatus(selectedJob.id, 'pending')"
                :loading="jobLoadingStates[selectedJob.id]"
                text="Move to Pending"
                loading-text="Moving..."
                variant="warning"
                size="md"
              />
              <button
                @click="selectedJob = null"
                class="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Image Modal -->
    <div v-if="showImageModal" class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999]" @click="closeImageModal">
      <div class="relative max-w-4xl max-h-[90vh] mx-4" @click.stop>
        <div class="bg-white rounded-lg overflow-hidden">
          <div class="flex justify-between items-center p-4 border-b">
            <h3 class="text-lg font-semibold text-gray-900">{{ currentProductName || (currentImages.length > 0 ? 'Product Images' : 'No Images Available') }}</h3>
            <button
              @click="closeImageModal"
              class="text-gray-400 hover:text-gray-600"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div class="relative">
            <!-- Main Image -->
            <div class="relative">
              <img 
                :src="getImageUrl(currentImages[currentImageIndex])" 
                :alt="`Product Image ${currentImageIndex + 1}`"
                class="w-full h-auto max-h-[70vh] object-contain"
                @error="handleImageError"
              />
              
              <!-- Navigation Arrows -->
              <button
                v-if="currentImages.length > 1"
                @click="previousImage"
                class="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-all"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <button
                v-if="currentImages.length > 1"
                @click="nextImage"
                class="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-all"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            
            <!-- Image Counter -->
            <div v-if="currentImages.length > 1" class="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
              {{ currentImageIndex + 1 }} / {{ currentImages.length }}
            </div>
          </div>
          
          <!-- Thumbnail Navigation -->
          <div v-if="currentImages.length > 1" class="p-4 border-t">
            <div class="flex gap-2 overflow-x-auto">
              <img 
                v-for="(image, index) in currentImages" 
                :key="index"
                :src="getImageUrl(image)" 
                :alt="`Thumbnail ${index + 1}`"
                class="w-16 h-16 object-contain rounded border-2 cursor-pointer transition-all"
                :class="index === currentImageIndex ? 'border-orange-500' : 'border-gray-200'"
                @click="currentImageIndex = index"
                @error="handleImageError"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import api from '../../services/api'
import { useUserStore } from '../../stores/user'
import LoadingButton from '../../components/LoadingButton.vue'

export default {
  name: 'WorkshopJobs',
  components: {
    LoadingButton
  },
  setup() {
    const userStore = useUserStore()
    const loading = ref(false)
    const jobs = ref([])
    const stats = ref({})
    const selectedJob = ref(null)
    
    // Loading states for individual job actions
    const jobLoadingStates = ref({})
    const filters = ref({
      status: '',
      dueDateFrom: '',
      dueDateTo: ''
    })
    
    // Search query state
    const searchQuery = ref({
      orderNo: ''
    })
    
    // Expanded job details state
    const expandedJobs = ref({})
    
    // Image modal state
    const showImageModal = ref(false)
    const currentImages = ref([])
    const currentImageIndex = ref(0)
    const currentProductName = ref('')

    const isAdmin = computed(() => userStore.isAdmin)

    // Incremented per request so a slow, older response can't overwrite a newer one
    let loadJobsRequestId = 0

    const loadJobs = async () => {
      const requestId = ++loadJobsRequestId
      loading.value = true
      try {
        const params = {}
        if (filters.value.status) params.status = filters.value.status
        if (filters.value.dueDateFrom) params.dueDateFrom = filters.value.dueDateFrom
        if (filters.value.dueDateTo) params.dueDateTo = filters.value.dueDateTo

        // Add search parameters
        if (searchQuery.value.orderNo) params.orderNo = searchQuery.value.orderNo

        // The board shows every job, so fetch all pages
        const allJobs = []
        let page = 1
        let response
        do {
          response = await api.get('/workshop/jobs', { params: { ...params, page } })
          if (requestId !== loadJobsRequestId) return
          allJobs.push(...(response.data.jobs || []))
          page++
        } while (page <= (response.data.pagination?.totalPages || 1))

        jobs.value = allJobs
        stats.value = response.data.stats || {}
      } catch (error) {
        if (requestId !== loadJobsRequestId) return
        console.error('Failed to load jobs:', error)
      } finally {
        if (requestId === loadJobsRequestId) loading.value = false
      }
    }

    const refreshJobs = () => {
      loadJobs()
    }
    
    // Debounce search so we don't hit the server on every keystroke
    let searchTimer = null
    const searchJobs = () => {
      clearTimeout(searchTimer)
      searchTimer = setTimeout(loadJobs, 300)
    }

    const clearFilters = () => {
      clearTimeout(searchTimer)
      searchQuery.value = {
        orderNo: ''
      }
      loadJobs()
    }

    const toggleJobDetails = (jobId) => {
      expandedJobs.value[jobId] = !expandedJobs.value[jobId]
    }

    const getJobsByStatus = (status) => {
      return jobs.value.filter(job => job.status === status)
    }

    const viewJobDetails = async (jobId) => {
      try {
        const response = await api.get(`/workshop/jobs/${jobId}`)
        selectedJob.value = response.data.job
      } catch (error) {
        console.error('Failed to load job details:', error)
      }
    }

    const updateJobStatus = async (jobId, newStatus) => {
      try {
        // Set loading state for this specific job
        jobLoadingStates.value[jobId] = true
        
        await api.patch(`/workshop/jobs/${jobId}/status`, {
          status: newStatus,
          notes: `Status updated to ${newStatus}`
        })
        
        // Refresh jobs after status update
        await loadJobs()
        
        // Close modal if open
        if (selectedJob.value && selectedJob.value.id === jobId) {
          selectedJob.value = null
        }
      } catch (error) {
        console.error('Failed to update job status:', error)
        // You could add a toast notification here for better UX
      } finally {
        // Clear loading state
        jobLoadingStates.value[jobId] = false
      }
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }

    const formatStatus = (status) => {
      const statusMap = {
        'pending': 'Pending',
        'in_workshop': 'In Progress',
        'completed': 'Completed',
        'assigned': 'Assigned'
      }
      return statusMap[status] || status
    }

    const getStatusClass = (status) => {
      const classMap = {
        'pending': 'bg-yellow-100 text-yellow-800',
        'in_workshop': 'bg-blue-100 text-blue-800',
        'completed': 'bg-green-100 text-green-800',
        'assigned': 'bg-purple-100 text-purple-800'
      }
      return classMap[status] || 'bg-gray-100 text-gray-800'
    }

    // Image modal functions
    const viewImageModal = (images, startIndex = 0, productName = '') => {
      console.log('Opening image modal:', { images, startIndex, imagesLength: images?.length, productName })
      currentImages.value = images
      currentImageIndex.value = startIndex
      currentProductName.value = productName
      showImageModal.value = true
      console.log('Modal state after opening:', { showImageModal: showImageModal.value, currentImages: currentImages.value, currentProductName: currentProductName.value })
    }

    const closeImageModal = () => {
      showImageModal.value = false
      currentImages.value = []
      currentImageIndex.value = 0
      currentProductName.value = ''
    }

    const nextImage = () => {
      if (currentImageIndex.value < currentImages.value.length - 1) {
        currentImageIndex.value++
      } else {
        currentImageIndex.value = 0
      }
    }

    const previousImage = () => {
      if (currentImageIndex.value > 0) {
        currentImageIndex.value--
      } else {
        currentImageIndex.value = currentImages.value.length - 1
      }
    }

    // Create a stable image URL cache to prevent flickering
    const imageUrlCache = new Map()
    
    const getImageUrl = (imageName) => {
      if (!imageName) return ''
      
      // Check if we have a cached URL for this image
      if (imageUrlCache.has(imageName)) {
        return imageUrlCache.get(imageName)
      }
      
      // If it's already a full URL, return as is
      if (imageName.startsWith('http://') || imageName.startsWith('https://')) {
        imageUrlCache.set(imageName, imageName)
        return imageName
      }
      
      // Use the nginx proxy for uploads
      const baseUrl = import.meta.env.VITE_API_URL || ''
      const url = `${baseUrl}/uploads/${imageName}`
      imageUrlCache.set(imageName, url)
      return url
    }

    const handleImageError = (event) => {
      // Set a fallback image when the actual image fails to load
      event.target.src = 'data:image/svg+xml;base64,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'
    }

    // Helper methods for weight calculations
    const getTotalQuantity = (items) => {
      if (!items || !Array.isArray(items)) return 0
      return items.reduce((total, item) => total + (item.quantity || item.qty || 0), 0)
    }

    const getTotalGrossWeight = (items) => {
      if (!items || !Array.isArray(items)) return 0
      return items.reduce((total, item) => {
        const quantity = item.quantity || item.qty || 0
        const grossWeight = parseFloat(item.gross_weight) || 0
        return total + (grossWeight * quantity)
      }, 0).toFixed(2)
    }

    const getTotalNetWeight = (items) => {
      if (!items || !Array.isArray(items)) return 0
      return items.reduce((total, item) => {
        const quantity = item.quantity || item.qty || 0
        const netWeight = parseFloat(item.net_weight) || 0
        return total + (netWeight * quantity)
      }, 0).toFixed(2)
    }

    const getItemGrossTotal = (item) => {
      const quantity = item.quantity || item.qty || 0
      const grossWeight = parseFloat(item.gross_weight) || 0
      return (grossWeight * quantity).toFixed(2)
    }

    const getItemNetTotal = (item) => {
      const quantity = item.quantity || item.qty || 0
      const netWeight = parseFloat(item.net_weight) || 0
      return (netWeight * quantity).toFixed(2)
    }

    onMounted(() => {
      loadJobs()
    })

    return {
      loading,
      jobs,
      stats,
      selectedJob,
      jobLoadingStates,
      filters,
      searchQuery,
      expandedJobs,
      isAdmin,
      showImageModal,
      currentImages,
      currentImageIndex,
      currentProductName,
      imageUrlCache,
      loadJobs,
      refreshJobs,
      clearFilters,
      searchJobs,
      toggleJobDetails,
      getJobsByStatus,
      viewJobDetails,
      updateJobStatus,
      formatDate,
      formatStatus,
      getStatusClass,
      viewImageModal,
      closeImageModal,
      nextImage,
      previousImage,
      getImageUrl,
      handleImageError,

      // Weight calculation methods
      getTotalQuantity,
      getTotalGrossWeight,
      getTotalNetWeight,
      getItemGrossTotal,
      getItemNetTotal
    }
  }
}
</script> 